/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import NoMoreRegister from '../../component/NoMoreRegister';
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import TextFieldSelectBox from '../../component/SelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
import {
  PrimaryButton,
  AddButton,
  SecondaryButton,
} from '../../component/BasicButton';
//API import
import {
  getCompositionsMoubusUnit,
  postCompositionsMoubusUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {format, limit, pullDownMenu} from '../../define';
import {modbusInfoSort} from '../../functions/sort';

/**
 * 室外機追加ダイアログ
 * @param props props
 * @returns JSX.Element
 */
const OUAddDialog = (props: {
  /** 室外機追加ボタンの名前 */
  addButtonName: string;
  /** 室外機の情報 */
  modbusInfo: apiIF.res.getCompositionsMoubusUnit;
  /** 室外機の情報（set関数） */
  setModbusInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsMoubusUnit | null>
  >;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID, gwID} = useParams();

  const [ouName, setOUName] = useState(''); //室外機の名前
  const [ipAddress, setIPAddress] = useState(''); //IPアドレス
  //prettier-ignore
  const [ouModel, setOUModel] = useState<apiIF.reqParameters.postCompositionsMoubusUnit['outdoor-unit-type']>('HCCVQ'); //室外機のタイプ
  //prettier-ignore
  const [ouPowerSupply, setOUPowerSupply] = useState<apiIF.reqParameters.postCompositionsMoubusUnit['power-supply']>('threePhase'); //電源の仕様
  const [ouVoltage, setOUVoltage] = useState('0'); //入力電圧
  const [isOpenDialog, setIsOpenDialog] = useState(false); //ダイアログ表示フラグ（追加ダイアログ・登録超過ダイアログ）
  const [okBtnDisabled, setOKBtnDisabled] = useState(true); //OKボタン非表示フラグ
  const [ouNumber, setOUNumber] = useState(0); //室外機台数
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [ouNameError, setOUNameError] = useState(false); //室外機の名前
  const [ipAddressError, setIPAddressError] = useState(false); //IPアドレス
  const [ouVoltageError, setOUVoltageError] = useState(false); //入力電圧

  //入力項目のチェック
  useEffect(() => {
    if (
      ouName === '' ||
      ipAddress === '' ||
      ouNameError ||
      apiWait ||
      ouVoltageError ||
      ipAddressError
    ) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [ouName, ipAddress, ouVoltageError, ouNameError, apiWait, ipAddressError]);

  //登録台数を算出
  useEffect(() => {
    //室外機の情報を取得できない場合
    if (props.modbusInfo === null) {
      return;
    }

    setOUNumber(props.modbusInfo['outdoor-unit-informations'].length);
  }, [props.modbusInfo]);

  /**
   * ADD HCCVQ outdoor unitボタン押下時処理
   */
  const handleClickOpen = () => {
    setIsOpenDialog(true);
  };

  /**
   * OKボタン押下時処理
   */
  const addOU = () => {
    //パスパラメータを取得できない場合
    if (siteID === undefined || gwID === undefined) {
      return;
    }

    setAPIWait(true);

    //室外機を追加
    const addData: apiIF.reqParameters.postCompositionsMoubusUnit = {
      'outdoor-unit-name': ouName,
      'ip-address': ipAddress,
      'outdoor-unit-type': ouModel,
      'power-supply': ouPowerSupply,
      'input-voltage': ouVoltage,
    };
    postCompositionsMoubusUnit(siteID, gwID, addData)
      .then(() => {
        //室内外機の情報を取得
        return getCompositionsMoubusUnit(siteID, gwID, {'max-result': 400});
      })
      .then(response => {
        response = modbusInfoSort(response);
        props.setModbusInfo(response);
        props.setHasConfigGW(props.isBilling); //課金している場合のみ表示
        handleClose();
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setIsOpenDialog(false);
    setOUName('');
    setIPAddress('');
    setOUVoltage('0');
    setOUPowerSupply('threePhase');
    setOKBtnDisabled(true);
  };

  return (
    <>
      {/* 室外機追加ボタン */}
      <AddButton
        onClick={handleClickOpen}
        id="OuAdd"
        buttonText={props.addButtonName}
      />
      {ouNumber >= limit.REGISTRATION_NUM_MODBUS_OU_MAX ? (
        <NoMoreRegister
          open={isOpenDialog}
          setOpen={setIsOpenDialog}
          title={trans('NoMoreRegist.errorDlg.title')}
          text={trans('NoMoreRegist.errorDlg.msg')}
        />
      ) : (
        <>
          {/* ダイアログ */}
          <Dialog
            open={isOpenDialog}
            onClose={handleClose}
            aria-labelledby="form-dialog-title"
            id="addOuDialog">
            <DialogTitle id="form-dialog-title">
              {trans('OUAddDialog.modbus.dialogBox.title')}
            </DialogTitle>
            <DialogContent>
              {/* ダイアログに表示するメッセージ */}
              <DialogContentText>
                {trans('OUAddDialog.modbus.dialogBox.msg')}
              </DialogContentText>
              <Stack spacing={1.5}>
                {/* 室外機名称 */}
                <ChkInputTextbox
                  setValue={setOUName}
                  setError={setOUNameError}
                  chkLength={format.BASIC_LENGTH_MAX}
                  type="text"
                  label={trans(
                    'OUAddDialog.modbus.dialogBox.txtField.label.hccvqName'
                  )}
                  value={ouName}
                  id="addHccvqOuNameTextForm"
                  class="gwSetting__contents__textBox--unitName"
                  helperText=" "
                  errHelperText={trans(
                    'OUAddDialog.modbus.dialogBox.txtField.errHelperTxt'
                  )}
                  variant="standard"
                  autoFocus={true}
                  margin="dense"
                  chkInputProhibitedCharacter={format.BASIC_PATTERN_S3_POLICY}
                />
                {/* IPアドレス */}
                <ChkInputTextbox
                  setValue={setIPAddress}
                  setError={setIPAddressError}
                  chkInputTxtOK={format.IPADDRESS_PATTERN_OK}
                  type="text"
                  label={trans(
                    'OUAddDialog.modbus.dialogBox.txtField.label.ipAddress'
                  )}
                  value={ipAddress}
                  id="addHccvqIPAddressTextForm"
                  class="gwSetting__contents__textBox--ipAddress"
                  helperText=" "
                  errHelperText={trans(
                    'OUAddDialog.modbus.dialogBox.txtField.errHelperTxt'
                  )}
                  variant="standard"
                  margin="dense"
                />
                {/* 室外機タイプ */}
                <TextFieldSelectBox
                  label={trans(
                    'OUAddDialog.modbus.dialogBox.txtField.label.outdoorType'
                  )}
                  choices={pullDownMenu.MODBUS_OU_TYPE}
                  setValue={setOUModel}
                  default={ouModel}
                  id="mv-ouTypeSelect"
                  class="gwSetting__contents__selectBox--modbusOUType"
                />
                {/* 電圧 */}
                <ChkInputTextbox
                  setValue={setOUVoltage}
                  setError={setOUVoltageError}
                  chkInputTxtOK={format.VOLTAGE_PATTERN_OK}
                  type="text"
                  label={trans(
                    'OUAddDialog.modbus.dialogBox.txtField.label.voltage'
                  )}
                  value={ouVoltage}
                  id="mv-inputOuVoltage"
                  class="gwSetting__contents__textBox--voltage"
                  helperText=" "
                  errHelperText={trans(
                    'OUAddDialog.modbus.dialogBox.txtField.errHelperTxt'
                  )}
                  variant="standard"
                />
                {/* 電源の種類 */}
                <TextFieldSelectBox
                  label={trans(
                    'OUAddDialog.modbus.dialogBox.txtField.label.powerSupply'
                  )}
                  choices={pullDownMenu.POWER_SUPPLY_TYPE}
                  setValue={setOUPowerSupply}
                  default={ouPowerSupply}
                  id="mv-ouAddPowSplySelect"
                  class="gwSetting__contents__selectBox--powerSupply"
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <SecondaryButton
                disabled={apiWait}
                onClick={handleClose}
                buttonText={trans(
                  'OUAddDialog.modbus.dialogBox.buttonTxt.cancel'
                )}
                id={'mv-ouAddCancelBtn'}
              />
              <PrimaryButton
                onClick={addOU}
                buttonText={trans('OUAddDialog.modbus.dialogBox.buttonTxt.ok')}
                id="mv-ouAddOkBtn"
                disabled={okBtnDisabled}
                className="BtnOk"
                apiWait={apiWait}
              />
            </DialogActions>
          </Dialog>
          <APIErrorMessage
            open={isOpenErrorDialog}
            setOpen={setIsOpenErrorDialog}
            id={'mv-ouAddErrDlgBtn'}
          />
        </>
      )}
    </>
  );
};
export default OUAddDialog;
